/**
 * Tracking page — แสดงสถานะพัสดุที่กำลังส่ง
 * Worker มี Q_TRACKING queue + cron 30min poll Thailand Post API
 *
 * Phase 1 (MVP — ตอนนี้): แสดง orders ที่ status='shipped' หรือ 'delivered' + tracking_code (จาก orders table)
 * Phase 2 (อนาคต): real-time tracking timeline จาก carrier API
 */
import { useQuery } from '@tanstack/react-query';
import { Truck, RefreshCw, Package, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface Order {
  id: number;
  orderNo: string | null;
  status: string;
  total: number | null;
  createdAt: string;
}

async function fetchOrders() {
  const base = import.meta.env.VITE_API_BASE || 'https://cher-fb-bot.nayaa.workers.dev';
  const res = await fetch(`${base}/api/admin/orders`, { credentials: 'include' });
  if (!res.ok) throw new Error(`orders ${res.status}`);
  return res.json() as Promise<{ orders: Order[] }>;
}

const STATUS_LABEL: Record<string, { label: string; color: string; icon: typeof Truck }> = {
  paid: { label: 'รอจัดส่ง', color: 'bg-blue-100 text-blue-700', icon: Package },
  shipped: { label: 'กำลังส่ง', color: 'bg-purple-100 text-purple-700', icon: Truck },
  delivered: { label: 'ถึงปลายทาง', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  completed: { label: 'สำเร็จ', color: 'bg-emerald-200 text-emerald-800', icon: CheckCircle2 },
};

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function TrackingPage() {
  const q = useQuery({ queryKey: ['orders-tracking'], queryFn: fetchOrders, staleTime: 30_000 });

  // Filter เฉพาะที่อยู่ในขั้นส่ง (paid/shipped/delivered/completed)
  const shipping = (q.data?.orders ?? []).filter((o) =>
    ['paid', 'shipped', 'delivered', 'completed'].includes(o.status),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={20} />
          <h2 className="text-xl font-bold">ติดตามพัสดุ</h2>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()}>
          <RefreshCw size={14} className={`mr-1 ${q.isFetching ? 'animate-spin' : ''}`} /> รีเฟรช
        </Button>
      </div>

      <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 text-xs text-blue-800">
        🚚 <strong>Worker poll สถานะพัสดุอัตโนมัติทุก 30 นาที</strong> จาก Thailand Post API.
        เมื่อสถานะอัพเดทถึงปลายทาง → บอตจะแจ้งลูกค้าผ่าน Messenger อัตโนมัติ.
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground">กำลังโหลด…</div>}
      {q.isError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          ❌ โหลดไม่สำเร็จ: {String(q.error)}
        </div>
      )}

      {q.data && shipping.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Truck size={48} className="mx-auto opacity-30 mb-3" />
          <div className="text-sm">ยังไม่มีพัสดุที่กำลังส่ง</div>
          <div className="text-xs mt-1">
            ออเดอร์ที่เปลี่ยนสถานะเป็น "จ่ายแล้ว" จะแสดงที่นี่
          </div>
        </div>
      )}

      {shipping.length > 0 && (
        <div className="grid gap-3">
          {shipping.map((o) => {
            const st = STATUS_LABEL[o.status] ?? { label: o.status, color: 'bg-gray-100', icon: Package };
            const Icon = st.icon;
            return (
              <div key={o.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                <Icon size={24} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{o.orderNo ?? `#${o.id}`}</span>
                    <Badge className={st.color}>{st.label}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    สร้างเมื่อ {fmtDate(o.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
